import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import dropin from "braintree-web-drop-in";

const API_BASE_URL = "https://api.itrustsecure.com";

function getToken() { return localStorage.getItem("itrust_token"); }
function authHeaders() { return { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` }; }

export default function CheckoutPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [dropinInstance, setDropinInstance] = useState(null);
  const dropinRef = useRef(null);

  const orderId = searchParams.get("orderId");

  useEffect(() => {
    if (orderId) loadOrder();
    else { setError("Invalid checkout link."); setLoading(false); }
  }, [orderId]);

  async function loadOrder() {
    try {
      const token = getToken();
      if (!token) { navigate("/login"); return; }

      const res = await fetch(`${API_BASE_URL}/orders/${orderId}`, { headers: authHeaders() });
      if (!res.ok) { setError("Order not found."); setLoading(false); return; }
      const data = await res.json();
      setOrder(data);

      // Get Braintree client token
      const tokenRes = await fetch(`${API_BASE_URL}/braintree/token`, { headers: authHeaders() });
      const tokenData = await tokenRes.json();
      if (!tokenRes.ok) { setError("Could not initialize payment."); setLoading(false); return; }

      setLoading(false);

      // Initialize Braintree Drop-in
      setTimeout(async () => {
        if (dropinRef.current) {
          try {
            const instance = await dropin.create({
              authorization: tokenData.clientToken,
              container: dropinRef.current,
              card: {
                cardholderName: { required: true }
              },
            });
            setDropinInstance(instance);
          } catch (err) {
            setError("Could not load payment form. Please refresh and try again.");
          }
        }
      }, 100);

    } catch (err) {
      setError("Something went wrong loading your order.");
      setLoading(false);
    }
  }

  async function handlePay() {
    if (!dropinInstance) return;
    setProcessing(true);
    setError("");

    try {
      const { nonce } = await dropinInstance.requestPaymentMethod();

      const res = await fetch(`${API_BASE_URL}/braintree/checkout`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          orderId,
          nonce,
          amount: Number(order.totalAmount),
          platformFee: Number(order.platformFee),
        })
      });

      const data = await res.json();
      if (!res.ok) { setError(data.error || "Payment failed. Please try again."); return; }

      setSuccess(true);
    } catch (err) {
      setError("Payment failed. Please check your card details and try again.");
    } finally {
      setProcessing(false);
    }
  }

  const styles = {
    root: { fontFamily: "'Segoe UI', Arial, sans-serif", background: "#0a0f1e", color: "#f0f4ff", minHeight: "100vh", display: "flex", flexDirection: "column" },
    nav: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px 40px", borderBottom: "1px solid rgba(255,255,255,0.08)" },
    navLogo: { fontSize: 20, fontWeight: 800, color: "#fff", cursor: "pointer" },
    navLogoAccent: { color: "#3b82f6" },
    main: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" },
    card: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 20, padding: "40px", width: "100%", maxWidth: 500 },
    title: { fontSize: 24, fontWeight: 800, marginBottom: 6 },
    subtitle: { fontSize: 14, color: "rgba(255,255,255,0.5)", marginBottom: 28 },
    summaryBox: { background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 12, padding: 16, marginBottom: 24 },
    summaryRow: { display: "flex", justifyContent: "space-between", fontSize: 13, color: "rgba(255,255,255,0.6)", marginBottom: 8 },
    totalRow: { display: "flex", justifyContent: "space-between", fontSize: 18, fontWeight: 800, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.08)" },
    protectionBadge: { display: "flex", alignItems: "center", gap: 8, background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: 10, padding: "10px 14px", marginBottom: 20, fontSize: 13, color: "#60a5fa" },
    dropinWrap: { marginBottom: 20 },
    payBtn: { width: "100%", padding: "16px", background: processing ? "rgba(59,130,246,0.4)" : "#3b82f6", color: "#fff", border: "none", borderRadius: 12, fontSize: 16, fontWeight: 700, cursor: processing ? "not-allowed" : "pointer", marginTop: 8 },
    errorBox: { background: "rgba(239,68,68,0.1)", border: "1px solid rgba(239,68,68,0.3)", color: "#fca5a5", padding: "10px 14px", borderRadius: 8, fontSize: 13, marginBottom: 16 },
    successCard: { textAlign: "center" },
    successIcon: { fontSize: 64, marginBottom: 16 },
    successTitle: { fontSize: 24, fontWeight: 800, marginBottom: 8 },
    successSubtitle: { fontSize: 14, color: "rgba(255,255,255,0.5)", marginBottom: 24 },
    successBtn: { background: "#3b82f6", color: "#fff", border: "none", padding: "14px 28px", borderRadius: 12, fontSize: 15, fontWeight: 700, cursor: "pointer" },
  };

  if (success) return (
    <div style={styles.root}>
      <nav style={styles.nav}>
        <div style={styles.navLogo} onClick={() => navigate("/home")}>
          i<span style={styles.navLogoAccent}>Trust Secure</span>
        </div>
      </nav>
      <div style={styles.main}>
        <div style={{ ...styles.card, ...styles.successCard }}>
          <div style={styles.successIcon}>🎉</div>
          <div style={styles.successTitle}>Payment Successful!</div>
          <div style={styles.successSubtitle}>
            Your payment has been processed. The seller has been notified and will fulfill your order.
          </div>
          <button style={styles.successBtn} onClick={() => navigate("/orders")}>
            View My Orders
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div style={styles.root}>
      <nav style={styles.nav}>
        <div style={styles.navLogo} onClick={() => navigate("/home")}>
          i<span style={styles.navLogoAccent}>Trust Secure</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.5)", fontSize: 13 }}>🔒 Secure Checkout</span>
      </nav>

      <div style={styles.main}>
        <div style={styles.card}>
          <div style={styles.title}>Complete Payment</div>
          <div style={styles.subtitle}>Enter your card details below</div>

          {loading ? (
            <div style={{ textAlign: "center", padding: 40, color: "rgba(255,255,255,0.5)" }}>
              Loading payment form...
            </div>
          ) : error && !order ? (
            <div style={styles.errorBox}>{error}</div>
          ) : (
            <>
              {order && (
                <div style={styles.summaryBox}>
                  <div style={styles.summaryRow}>
                    <span>Order</span>
                    <span>#{order.id?.slice(0, 8)}</span>
                  </div>
                  <div style={styles.summaryRow}>
                    <span>Seller</span>
                    <span>{order.seller?.fullName || "—"}</span>
                  </div>
                  <div style={styles.summaryRow}>
                    <span>Platform Fee</span>
                    <span>${Number(order.platformFee).toFixed(2)}</span>
                  </div>
                  <div style={styles.totalRow}>
                    <span>Total</span>
                    <span style={{ color: "#3b82f6" }}>${Number(order.totalAmount).toFixed(2)}</span>
                  </div>
                </div>
              )}

              <div style={styles.protectionBadge}>
                🔒 Seller receives payment only after you confirm delivery
              </div>

              {error && <div style={styles.errorBox}>{error}</div>}

              {/* Braintree Drop-in container */}
              <div style={styles.dropinWrap}>
                <div ref={dropinRef} />
              </div>

              <button
                style={styles.payBtn}
                onClick={handlePay}
                disabled={processing || !dropinInstance}
              >
                {processing ? "Processing..." : `Pay $${order ? Number(order.totalAmount).toFixed(2) : "—"}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}